import React, { useState, useContext } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FirebaseAuthContext } from '../../auth/FirebaseAuth';
import { useNavigate } from 'react-router-dom';
import { showToastr } from '../ToastrMessage/Toast.helper';
import useTranslation from '../../translations/use-translation';
import './CSCOwnerAuth.styles.scss';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman & Nicobar', 'Chandigarh', 'Delhi', 'Jammu & Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

const emptyRegisterForm = {
  centerName: '', ownerName: '', email: '', password: '',
  mobile: '', address: '', villageTown: '',
  pincode: '', city: '', state: '', mapLocation: '',
};
const emptyLoginForm = { email: '', password: '' };
const emptyErrors = {
  centerName: '', ownerName: '', email: '', password: '',
  mobile: '', address: '', villageTown: '',
  pincode: '', city: '', state: '',
};

const CSCOwnerAuth = ({ onClose, onBack }) => {
  const translate = useTranslation();
  const navigate = useNavigate();
  const { signUpUser, signInUser } = useContext(FirebaseAuthContext);

  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [loginForm, setLoginForm] = useState(emptyLoginForm);
  const [errors, setErrors] = useState(emptyErrors);

  const switchMode = (next) => {
    setMode(next);
    setErrors(emptyErrors);
    setRegisterForm(emptyRegisterForm);
    setLoginForm(emptyLoginForm);
  };

  const handleRegisterChange = (e) =>
    setRegisterForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleLoginChange = (e) =>
    setLoginForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const validateRegister = () => {
    const e = { ...emptyErrors };
    if (!registerForm.centerName.trim())      e.centerName  = 'CSC center name is required';
    if (!registerForm.ownerName.trim())       e.ownerName   = 'Owner name is required';
    if (!registerForm.email.trim())            e.email       = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(registerForm.email)) e.email = 'Enter a valid email';
    if (!registerForm.password)                e.password    = 'Password is required';
    else if (registerForm.password.length < 6) e.password    = 'Minimum 6 characters';
    if (!registerForm.mobile.trim())           e.mobile      = 'Mobile number is required';
    else if (!/^\d{10}$/.test(registerForm.mobile)) e.mobile = 'Enter a valid 10-digit number';
    if (!registerForm.address.trim())          e.address     = 'Address is required';
    if (!registerForm.villageTown.trim())      e.villageTown = 'Village / Town is required';
    if (!registerForm.pincode.trim())          e.pincode     = 'Pincode is required';
    else if (!/^\d{6}$/.test(registerForm.pincode)) e.pincode = 'Enter a valid 6-digit pincode';
    if (!registerForm.city.trim())             e.city        = 'City is required';
    if (!registerForm.state)                   e.state       = 'State is required';
    setErrors(e);
    return Object.values(e).every((v) => !v);
  };

  const validateLogin = () => {
    const e = { ...emptyErrors };
    if (!loginForm.email.trim())  e.email    = 'Email is required';
    if (!loginForm.password)      e.password = 'Password is required';
    setErrors(e);
    return !e.email && !e.password;
  };

  const saveCscProfile = (data) => {
    localStorage.setItem('cscOwnerProfile', JSON.stringify(data));
    localStorage.setItem('cscOwnerLoggedIn', 'true');
  };

  const handleRegister = async () => {
    if (!validateRegister()) return;
    setLoading(true);
    try {
      await signUpUser(registerForm.email, registerForm.password);
      saveCscProfile({
        centerName:   registerForm.centerName,
        ownerName:    registerForm.ownerName,
        email:        registerForm.email,
        mobile:       registerForm.mobile,
        address:      registerForm.address,
        villageTown:  registerForm.villageTown,
        pincode:      registerForm.pincode,
        city:         registerForm.city,
        state:        registerForm.state,
        mapLocation:  registerForm.mapLocation,
        registeredAt: new Date().toISOString(),
      });
      onClose();
      navigate('/csc-portal');
    } catch (error) {
      if (error.code === 'auth/email-already-in-use')
        setErrors((p) => ({ ...p, email: 'Email already registered' }));
      else if (error.code === 'auth/network-request-failed')
        showToastr('error', 'Please check your internet connection.');
      else
        showToastr('error', 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!validateLogin()) return;
    setLoading(true);
    try {
      await signInUser(loginForm.email, loginForm.password);
      localStorage.setItem('cscOwnerLoggedIn', 'true');
      onClose();
      navigate('/csc-portal');
    } catch (error) {
      if (error.code === 'auth/invalid-login-credentials' || error.code === 'auth/wrong-password')
        setErrors((p) => ({ ...p, email: 'Invalid email or password' }));
      else if (error.code === 'auth/network-request-failed')
        showToastr('error', 'Please check your internet connection.');
      else if (error.code === 'auth/too-many-requests')
        showToastr('error', 'Too many failed attempts. Please try again later.');
      else
        showToastr('error', 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mode === 'register' ? handleRegister() : handleLogin();
  };

  return (
    <div className="csc-auth">

      {/* ── Back button ── */}
      <button
        type="button"
        className="csc-auth__back"
        onClick={onBack}
        aria-label="Back to account type selection"
      >
        ← Back
      </button>

      {/* ── Header (same structure as Login) ── */}
      <div className="csc-auth__header">
        <div className="csc-auth__brand-row">
          <span className="csc-auth__brand-icon">
            <StorefrontOutlinedIcon />
          </span>
          <span className="csc-auth__brand-name">CSC Owner Portal</span>
        </div>
        <h2 className="csc-auth__title">
          {mode === 'register' ? translate('CSC_REGISTER_HEADING') : translate('CSC_LOGIN_HEADING')}
        </h2>
        <p className="csc-auth__subtitle">
          {mode === 'register' ? translate('CSC_REGISTER_SUBTITLE') : translate('CSC_LOGIN_SUBTITLE')}
        </p>
      </div>

      {/* ── Tab switcher (pill — same as Login) ── */}
      <div className="csc-auth__tabs" role="tablist">
        <button
          role="tab" type="button"
          aria-selected={mode === 'login'}
          className={`csc-auth__tab${mode === 'login' ? ' csc-auth__tab--active' : ''}`}
          onClick={() => switchMode('login')}
        >
          {translate('CSC_LOGIN_TAB')}
        </button>
        <button
          role="tab" type="button"
          aria-selected={mode === 'register'}
          className={`csc-auth__tab${mode === 'register' ? ' csc-auth__tab--active' : ''}`}
          onClick={() => switchMode('register')}
        >
          {translate('CSC_REGISTER_TAB')}
        </button>
      </div>

      {/* ── Form ── */}
      <form className="csc-auth__form" onSubmit={handleSubmit} noValidate>

        {/* ─ Register fields ─ */}
        {mode === 'register' && (
          <>
            <TextField
              size="small" variant="outlined" fullWidth
              label={translate('CSC_CENTER_NAME')}
              name="centerName" required
              value={registerForm.centerName}
              onChange={handleRegisterChange}
              error={!!errors.centerName}
              helperText={errors.centerName}
              placeholder="e.g. Rajesh Kumar Seva Kendra"
            />

            <TextField
              size="small" variant="outlined" fullWidth
              label={translate('OWNER_NAME')}
              name="ownerName" required
              value={registerForm.ownerName}
              onChange={handleRegisterChange}
              error={!!errors.ownerName}
              helperText={errors.ownerName}
            />

            <div className="csc-auth__row--2col">
              <TextField
                size="small" variant="outlined" fullWidth
                label={translate('EMAIL_LABEL')}
                name="email" type="email" required
                value={registerForm.email}
                onChange={handleRegisterChange}
                error={!!errors.email}
                helperText={errors.email}
              />
              <TextField
                size="small" variant="outlined" fullWidth
                label={translate('MOBILE_NO')}
                name="mobile" required
                value={registerForm.mobile}
                onChange={handleRegisterChange}
                error={!!errors.mobile}
                helperText={errors.mobile}
                inputProps={{ maxLength: 10 }}
              />
            </div>

            <TextField
              size="small" variant="outlined" fullWidth
              label={translate('PASSWORD_LABEL')}
              name="password" required
              type={showPassword ? 'text' : 'password'}
              value={registerForm.password}
              onChange={handleRegisterChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end"
                      onClick={() => setShowPassword((s) => !s)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              size="small" variant="outlined" fullWidth
              label={translate('CSC_ADDRESS')}
              name="address" required
              value={registerForm.address}
              onChange={handleRegisterChange}
              error={!!errors.address}
              helperText={errors.address}
            />

            <div className="csc-auth__row--2col">
              <TextField
                size="small" variant="outlined" fullWidth
                label={translate('VILLAGE_TOWN')}
                name="villageTown" required
                value={registerForm.villageTown}
                onChange={handleRegisterChange}
                error={!!errors.villageTown}
                helperText={errors.villageTown}
              />
              <TextField
                size="small" variant="outlined" fullWidth
                label={translate('PINCODE')}
                name="pincode" required
                value={registerForm.pincode}
                onChange={handleRegisterChange}
                error={!!errors.pincode}
                helperText={errors.pincode}
                inputProps={{ maxLength: 6 }}
              />
            </div>

            <div className="csc-auth__row--2col">
              <TextField
                size="small" variant="outlined" fullWidth
                label={translate('CITY')}
                name="city" required
                value={registerForm.city}
                onChange={handleRegisterChange}
                error={!!errors.city}
                helperText={errors.city}
              />
              <TextField
                select
                size="small" variant="outlined" fullWidth
                label={translate('STATE')}
                name="state" required
                value={registerForm.state}
                onChange={handleRegisterChange}
                error={!!errors.state}
                helperText={errors.state}
              >
                {INDIAN_STATES.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </div>

            <TextField
              size="small" variant="outlined" fullWidth
              label={translate('MAP_LOCATION')}
              name="mapLocation"
              value={registerForm.mapLocation}
              onChange={handleRegisterChange}
              placeholder="Paste Google Maps link or enter coordinates"
            />
          </>
        )}

        {/* ─ Login fields ─ */}
        {mode === 'login' && (
          <>
            <TextField
              size="small" variant="outlined" fullWidth
              label={translate('EMAIL_LABEL')}
              name="email" type="email" required
              value={loginForm.email}
              onChange={handleLoginChange}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              size="small" variant="outlined" fullWidth
              label={translate('PASSWORD_LABEL')}
              name="password" required
              type={showPassword ? 'text' : 'password'}
              value={loginForm.password}
              onChange={handleLoginChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end"
                      onClick={() => setShowPassword((s) => !s)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}

        {/* ── Submit ── */}
        <Button
          fullWidth type="submit"
          className="csc-auth__submit"
          variant="contained"
          disableElevation
          disabled={loading}
          endIcon={loading && <CircularProgress color="inherit" size={16} />}
        >
          {mode === 'register'
            ? (loading ? 'Registering...' : translate('CSC_REGISTER_BTN'))
            : (loading ? 'Signing in...'  : translate('CSC_LOGIN_BTN'))}
        </Button>

      </form>
    </div>
  );
};

export default CSCOwnerAuth;
